// ColumnsPanel.jsx — the shared "⚙ Columns" control (Positions / Orders / Market Watch).
// Show/hide (checkbox), reorder by DRAG-AND-DROP (grab the ⠿ handle or anywhere on the row
// and drop it where it should go — rows shuffle live while dragging), plus ↑/↓ one-step
// buttons kept as the touch/mobile fallback (HTML5 drag doesn't fire on touchscreens), and Reset.
//
// Pages render ONE line — the button owns its open/close state and the panel:
//   <ColumnsButton cols={cols} isDark={isDark} />
import React, { useState } from 'react';

// The toolbar button + its panel, self-contained (no per-page state needed).
export function ColumnsButton({ cols, isDark }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(s => !s)} title="Customize columns (show/hide, drag to reorder)"
        className="px-2.5 py-0.5 text-[11px] font-semibold rounded border cursor-pointer bg-purple-500/15 text-purple-400 border-purple-500/30">
        ⚙ Columns
      </button>
      {open && <ColumnsPanel cols={cols} isDark={isDark} onClose={() => setOpen(false)} />}
    </div>
  );
}

export default function ColumnsPanel({ cols, isDark, onClose }) {
  const [dragKey, setDragKey] = useState(null);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
      <div style={{ position: 'absolute', top: '120%', left: 0, zIndex: 50, width: 280, maxHeight: 380, overflowY: 'auto',
           background: isDark ? '#13151e' : '#ffffff', border: `1px solid ${isDark ? '#1e2130' : '#e5e7eb'}`,
           borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', padding: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span className={`text-[11px] font-semibold ${isDark ? 'text-app-text-muted' : 'text-gray-600'}`}>Columns — drag to reorder</span>
          <button onClick={cols.reset} title="Reset to default layout"
            className="text-[10px] px-1.5 py-0.5 rounded border cursor-pointer bg-red-500/15 text-red-500 border-red-500/30">Reset</button>
        </div>
        {cols.layout.map((l, idx) => {
          const def = cols.baseByKey.get(l.key);
          if (!def) return null;
          const isDragging = dragKey === l.key;
          return (
            <div key={l.key}
              draggable
              onDragStart={e => { setDragKey(l.key); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', l.key); }}
              onDragEnter={() => { if (dragKey && dragKey !== l.key) cols.moveTo(dragKey, idx); }}
              onDragOver={e => e.preventDefault()}
              onDragEnd={() => setDragKey(null)}
              onDrop={e => { e.preventDefault(); setDragKey(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 2px', borderRadius: 4,
                       opacity: isDragging ? 0.45 : 1,
                       background: isDragging ? (isDark ? '#1e2130' : '#eef2ff') : 'transparent' }}>
              <span title="Drag to reorder" style={{ cursor: 'grab', userSelect: 'none', touchAction: 'none' }}
                className={`text-[12px] ${isDark ? 'text-app-text-dim' : 'text-gray-400'}`}>⠿</span>
              <input type="checkbox" checked={!l.hidden} onChange={() => cols.toggle(l.key)} title={l.hidden ? 'Show column' : 'Hide column'} />
              <span className={`text-[11px] flex-1 truncate ${isDark ? 'text-app-text' : 'text-gray-700'}`} title={def.label}>{def.label}</span>
              <button onClick={() => cols.move(l.key, -1)} disabled={idx === 0} title="Move up"
                className={`text-[11px] px-1 rounded border cursor-pointer ${isDark ? 'border-app-border text-app-text-muted' : 'border-gray-300 text-gray-500'} ${idx === 0 ? 'opacity-30' : ''}`}>↑</button>
              <button onClick={() => cols.move(l.key, 1)} disabled={idx === cols.layout.length - 1} title="Move down"
                className={`text-[11px] px-1 rounded border cursor-pointer ${isDark ? 'border-app-border text-app-text-muted' : 'border-gray-300 text-gray-500'} ${idx === cols.layout.length - 1 ? 'opacity-30' : ''}`}>↓</button>
            </div>
          );
        })}
      </div>
    </>
  );
}
